import { useEffect, useMemo, useState } from 'react'

import { Box, Checkbox, Stack, Typography, useTheme } from '@mui/material'

import { Button, Modal, FieldLabel, Alert } from '@repo/ui'
import {
  SHIPPING_BOX_PRESET_LIST,
  SHIPPING_BOX_PRESETS,
  DEFAULT_SHIPPING_BOX_PRESET,
  MOCK_PACKAGE_WEIGHT_OZ,
  computeShipmentWeightOz,
  type ShippingBoxPresetId,
} from 'constants/shippingBoxPresets'

import { SelectField, ProductCell } from 'components'
import { useCreateShipment } from 'hooks/api/useShopifyOrdersQuery'
import { getErrorMessage } from 'utils'

export interface CreateShipmentEligibleProduct {
  id: string
  title: string
  image?: string
  sku?: string
  packageName?: string
  /** Number of Vera packages assigned to this product (typically 1). */
  packageCount?: number
}

interface CreateShipmentModalProps {
  open: boolean
  onClose: () => void
  orderId: string
  /** Products with a package assigned and not yet grouped into a shipment. */
  eligibleProducts: CreateShipmentEligibleProduct[]
  /** Preselect these line item ids (single-product order: the lone product). */
  preselectedLineItemIds?: string[]
  onCreated: () => void
}

/**
 * VRG-138 REDESIGN (VERA-891): "Create shipment" — group products (each already
 * assigned a Vera package) into ONE shipment packed in ONE box-size preset.
 * Only products with a package assigned are eligible. The box + summed weight
 * (packages' mock weights + box tare) are set here at creation time.
 */
export const CreateShipmentModal = (props: CreateShipmentModalProps) => {
  const { open, onClose, orderId, eligibleProducts, preselectedLineItemIds, onCreated } = props
  const theme = useTheme()

  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [boxPreset, setBoxPreset] = useState<ShippingBoxPresetId>(DEFAULT_SHIPPING_BOX_PRESET)
  const [error, setError] = useState<string | null>(null)

  const createShipment = useCreateShipment()

  // Reset selection each time the modal opens: preselected ids if given, else
  // all eligible products when there's only one (single-product order).
  useEffect(() => {
    if (!open) return
    const initial = preselectedLineItemIds?.length
      ? preselectedLineItemIds
      : eligibleProducts.length === 1
        ? [eligibleProducts[0].id]
        : []
    setSelected(new Set(initial))
    setBoxPreset(DEFAULT_SHIPPING_BOX_PRESET)
    setError(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const toggle = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const allSelected = eligibleProducts.length > 0 && selected.size === eligibleProducts.length
  const toggleSelectAll = () => {
    setSelected(allSelected ? new Set() : new Set(eligibleProducts.map(p => p.id)))
  }

  const boxOptions = useMemo(
    () =>
      SHIPPING_BOX_PRESET_LIST.map(box => ({
        label: box.label,
        value: box.id,
        rightLabel: `${box.lengthIn} × ${box.widthIn} × ${box.heightIn} in`,
      })),
    [],
  )

  // Suggested total weight = summed mock weights of the selected products'
  // packages + box tare (a product may carry >1 package).
  const selectedPackageCount = eligibleProducts
    .filter(p => selected.has(p.id))
    .reduce((sum, p) => sum + (p.packageCount ?? 1), 0)
  const suggestedWeight = computeShipmentWeightOz(selectedPackageCount, boxPreset)

  const handleCreate = () => {
    setError(null)
    createShipment.mutate(
      { orderId, lineItemIds: Array.from(selected), boxPreset },
      {
        onSuccess: () => onCreated(),
        onError: err => setError(getErrorMessage(err)),
      },
    )
  }

  return (
    <Modal
      title="Create shipment"
      open={open}
      onClose={onClose}
      actions={
        <Stack direction="row" spacing={3}>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={createShipment.isPending}
            disabled={selected.size === 0}
            onClick={handleCreate}
          >
            Create shipment
          </Button>
        </Stack>
      }
    >
      <Stack spacing={5}>
        <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          Group products into one box and buy a single shipping label for them. Only products with a
          package assigned can be added.
        </Typography>

        {/* Product checklist — only eligible (package-assigned, un-shipped) products. */}
        <Stack spacing={2}>
          <FieldLabel
            label="Products"
            adornment={
              <Typography
                component="span"
                variant="body3"
                sx={{ cursor: 'pointer', color: 'brand.600', fontWeight: 500 }}
                onClick={toggleSelectAll}
              >
                {allSelected ? 'Deselect all' : 'Select all'}
              </Typography>
            }
          />
          <Stack
            spacing={0}
            sx={{
              border: `1px solid ${theme.palette.primary[100]}`,
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            {eligibleProducts.map((product, i) => (
              <Stack
                key={product.id}
                direction="row"
                alignItems="center"
                spacing={2}
                onClick={() => toggle(product.id)}
                sx={{
                  px: 3,
                  py: 2,
                  cursor: 'pointer',
                  borderTop: i === 0 ? 'none' : `1px solid ${theme.palette.primary[100]}`,
                  '&:hover': { bgcolor: theme.palette.action.hover },
                }}
              >
                <Checkbox checked={selected.has(product.id)} size="small" />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <ProductCell
                    product={{ title: product.title, sku: product.sku, images: product.image ? [product.image] : [] }}
                    size={40}
                  />
                </Box>
              </Stack>
            ))}
          </Stack>
        </Stack>

        {/* Box-size preset (global VERA presets). */}
        <Stack spacing={2}>
          <FieldLabel label="Shipping box size" />
          <SelectField
            options={boxOptions}
            value={boxPreset}
            onChange={value => setBoxPreset(value as ShippingBoxPresetId)}
          />
        </Stack>

        {/* Suggested total weight = summed package weights + box tare. */}
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="body3" color="text.secondary">
            Suggested total weight
          </Typography>
          <Typography variant="body2" fontWeight="medium">
            {suggestedWeight} oz
          </Typography>
        </Stack>
        <Typography variant="caption" color="text.secondary">
          {selectedPackageCount} package{selectedPackageCount === 1 ? '' : 's'} × {MOCK_PACKAGE_WEIGHT_OZ} oz +{' '}
          {SHIPPING_BOX_PRESETS[boxPreset].tareOz} oz box (mock weights).
        </Typography>

        {error && <Alert severity="error" description={error} />}
      </Stack>
    </Modal>
  )
}
